import { PillBadge, GhostButton } from "../Primitives";
import { ROLE_LABELS } from "../../lib/roles";
import { Pencil, Trash2, Paperclip } from "lucide-react";
import { SortableHeader } from "./SortableHeader";
import { statusTone, summarizeCertExpiry } from "./officerUtils";

/**
 * Dense list-view table of guards. Purely presentational — all handlers are
 * lifted to the parent so it can decide what a delete/edit/docs action means.
 */
export function GuardsTable({ items, totalCount, sortKey, sortDir, onSort, onEdit, onDelete, onOpenDocs }) {
  return (
    <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
      <table className="w-full text-sm" data-testid="guards-table">
        <thead className="border-b border-[#27272A]">
          <tr>
            <th className="px-2 py-3 w-[40px] text-center"><span className="label-mute">#</span></th>
            <SortableHeader k="badge_no" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Badge</SortableHeader>
            <SortableHeader k="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Name</SortableHeader>
            <SortableHeader k="role" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Role</SortableHeader>
            <SortableHeader k="phone" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Phone</SortableHeader>
            <th className="px-4 py-3 text-left"><span className="label-mute">Certifications</span></th>
            <SortableHeader k="hourly_rate" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Rate</SortableHeader>
            <SortableHeader k="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Status</SortableHeader>
            <th className="px-4 py-3 text-right"><span className="label-mute">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#27272A]">
          {items.map((g, idx) => {
            const certs = g.certifications || [];
            const { expired, nearing } = summarizeCertExpiry(certs);
            const names = certs.slice(0, 3).map((c) => typeof c === "string" ? c : c.name).join(", ");
            const dd = g.deactivation_date;
            const now = new Date(); now.setHours(0, 0, 0, 0);
            const past = dd && new Date(dd) <= now;
            return (
              <tr key={g.id} className="hover:bg-[#1c1c1e] transition-colors">
                <td className="px-2 py-3 text-center font-mono text-zinc-500" data-testid={`row-num-${g.id}`}>{idx + 1}</td>
                <td className="px-4 py-3 font-mono text-[#FF9F0A]">{g.badge_no}</td>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    <span>{g.name}</span>
                    {g.user_id && <span className="text-[9px] text-[#34C759] uppercase tracking-widest border border-[#34C759]/40 px-1 leading-tight">login</span>}
                  </div>
                  {g.username && (
                    <div
                      className="text-[10px] font-mono text-zinc-500 mt-0.5"
                      data-testid={`guard-username-${g.id}`}
                      title="Username used to log in"
                    >
                      @{g.username}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <PillBadge tone={g.role === "supervisor" ? "blue" : g.role === "dispatcher" ? "amber" : "default"}>
                    {ROLE_LABELS[g.role] || ROLE_LABELS.guard}
                  </PillBadge>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-400">{g.phone}</td>
                <td className="px-4 py-3 text-zinc-400" data-testid={`certs-cell-${g.id}`}>
                  {certs.length === 0 ? "—" : (
                    <div>
                      <div className="truncate max-w-[180px]">{names || `${certs.length} cert${certs.length === 1 ? "" : "s"}`}</div>
                      {(expired || nearing) > 0 && (
                        <div className="text-[10px] uppercase tracking-widest mt-0.5 inline-flex items-center gap-2">
                          {expired > 0 && <span className="text-[#FF3B30]" data-testid={`certs-expired-${g.id}`}>⛔ {expired} expired</span>}
                          {nearing > 0 && <span className="text-[#FF9F0A]" data-testid={`certs-nearing-${g.id}`}>⚠ {nearing} ≤30d</span>}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono">${g.hourly_rate}/hr</td>
                <td className="px-4 py-3">
                  {!dd ? (
                    <PillBadge tone={statusTone(g.status)}>{g.status.replace("_", " ")}</PillBadge>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      <PillBadge tone={past ? "red" : "amber"}>
                        {past ? "deactivated" : "deactivates"}
                      </PillBadge>
                      <span className="text-[10px] font-mono text-zinc-500">{dd}</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <GhostButton data-testid={`docs-guard-${g.id}`} onClick={() => onOpenDocs(g)}>
                      <Paperclip className="size-3.5" />
                    </GhostButton>
                    <GhostButton data-testid={`edit-guard-${g.id}`} onClick={() => onEdit(g)}>
                      <Pencil className="size-3.5" />
                    </GhostButton>
                    <GhostButton data-testid={`delete-guard-${g.id}`} onClick={() => onDelete(g.id)}>
                      <Trash2 className="size-3.5" />
                    </GhostButton>
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr><td colSpan={9} className="text-center py-16 text-zinc-500 scanlines">
              {totalCount === 0 ? "No officers on roster yet." : "No officers match the current filters."}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
